import type {
  TriggerItem,
  TriggerEvent,
  ConditionOperator,
  ActionType,
} from "./types";

export const TRIGGER_ITEM_LABELS: Record<
  TriggerItem,
  { label: string; desc: string }
> = {
  negocio: {
    label: "Negócio",
    desc: "Dispara com base em oportunidades e negócios no funil",
  },
  pessoa: {
    label: "Pessoa",
    desc: "Dispara com base em contatos e leads cadastrados",
  },
  organizacao: {
    label: "Organização",
    desc: "Dispara com base em empresas e contas clientes",
  },
  atividade: {
    label: "Atividade",
    desc: "Dispara com base em tarefas, reuniões ou ligações",
  },
};

export const TRIGGER_EVENT_LABELS: Record<
  TriggerEvent,
  { label: string; badge: string }
> = {
  criado: {
    label: "Criado",
    badge:
      "bg-emerald-500/10 text-emerald-600 border-emerald-500/30 dark:text-emerald-400",
  },
  atualizado: {
    label: "Atualizado",
    badge: "bg-blue-500/10 text-blue-600 border-blue-500/30 dark:text-blue-400",
  },
  excluido: {
    label: "Excluído",
    badge: "bg-rose-500/10 text-rose-600 border-rose-500/30 dark:text-rose-400",
  },
};

export const CONDITION_OPERATORS: Array<{
  value: ConditionOperator;
  label: string;
  category: "texto" | "numero" | "data" | "status" | "geral";
}> = [
  { value: "igual_a", label: "É igual a", category: "geral" },
  { value: "diferente_de", label: "É diferente de", category: "geral" },
  { value: "mudou_para", label: "Mudou para", category: "status" },
  { value: "contem", label: "Contém", category: "texto" },
  { value: "nao_contem", label: "Não contém", category: "texto" },
  { value: "comeca_com", label: "Começa com", category: "texto" },
  { value: "termina_com", label: "Termina com", category: "texto" },
  { value: "esta_vazio", label: "Está vazio", category: "geral" },
  { value: "nao_esta_vazio", label: "Não está vazio", category: "geral" },
  { value: "maior_que", label: "Maior que (>)", category: "numero" },
  { value: "menor_que", label: "Menor que (<)", category: "numero" },
  { value: "maior_ou_igual", label: "Maior ou igual (>=)", category: "numero" },
  { value: "menor_ou_igual", label: "Menor ou igual (<=)", category: "numero" },
  { value: "data_antes_de", label: "Data é antes de", category: "data" },
  { value: "data_depois_de", label: "Data é depois de", category: "data" },
  { value: "data_igual_a", label: "Data é igual a", category: "data" },
  {
    value: "valor_anterior_era",
    label: "Valor anterior era...",
    category: "status",
  },
];

export const ACTION_TYPE_LABELS: Record<
  ActionType,
  { label: string; desc: string; category: "crm" | "webhook" }
> = {
  criar_negocio: {
    label: "Criar Negócio",
    desc: "Cria uma nova oportunidade no funil de vendas",
    category: "crm",
  },
  atualizar_negocio: {
    label: "Atualizar Negócio",
    desc: "Atualiza etapa, valor ou campos do negócio",
    category: "crm",
  },
  excluir_negocio: {
    label: "Excluir Negócio",
    desc: "Remove o negócio do sistema",
    category: "crm",
  },
  criar_pessoa: {
    label: "Criar Pessoa",
    desc: "Cadastra um novo contato no CRM",
    category: "crm",
  },
  atualizar_pessoa: {
    label: "Atualizar Pessoa",
    desc: "Modifica dados do contato",
    category: "crm",
  },
  excluir_pessoa: {
    label: "Excluir Pessoa",
    desc: "Remove o contato do CRM",
    category: "crm",
  },
  criar_organizacao: {
    label: "Criar Organização",
    desc: "Cadastra uma nova empresa no CRM",
    category: "crm",
  },
  atualizar_organizacao: {
    label: "Atualizar Organização",
    desc: "Atualiza informações da empresa",
    category: "crm",
  },
  excluir_organizacao: {
    label: "Excluir Organização",
    desc: "Remove a organização",
    category: "crm",
  },
  criar_atividade: {
    label: "Criar Atividade",
    desc: "Gera tarefa, ligação ou reunião automática",
    category: "crm",
  },
  atualizar_atividade: {
    label: "Atualizar Atividade",
    desc: "Altera status ou prazo de uma atividade",
    category: "crm",
  },
  excluir_atividade: {
    label: "Excluir Atividade",
    desc: "Remove uma atividade",
    category: "crm",
  },
  criar_nota: {
    label: "Criar Nota",
    desc: "Insere uma anotação no histórico do registro",
    category: "crm",
  },
  webhook: {
    label: "Disparar Webhook HTTP",
    desc: "Envia requisição POST externa para n8n, Make, Zapier ou API",
    category: "webhook",
  },
};

export const COMMON_CRM_VARIABLES = [
  // ── Negócio Conectado (quando a automação é disparada por Atividade) ──
  {
    tag: "{{item.deal.name}}",
    label: "Negócio: Nome / Título",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.stage}}",
    label: "Negócio: Etapa do Funil",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.status}}",
    label: "Negócio: Status (aberto/ganho/perdido)",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.amount}}",
    label: "Negócio: Valor (R$)",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.valor_proposta}}",
    label: "Negócio: Valor da Proposta",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.category}}",
    label: "Negócio: Categoria",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.description}}",
    label: "Negócio: Descrição",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.temperatura}}",
    label: "Negócio: Temperatura",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.origem}}",
    label: "Negócio: Origem",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.expected_closing_date}}",
    label: "Negócio: Previsão de Fechamento",
    group: "Negócio Conectado",
  },
  {
    tag: "{{item.deal.motivo_perda}}",
    label: "Negócio: Motivo da Perda",
    group: "Negócio Conectado",
  },
  { tag: "{{item.deal.id}}", label: "Negócio: ID", group: "Negócio Conectado" },

  // ── Atividade ──
  {
    tag: "{{item.text}}",
    label: "Atividade: Assunto / Texto",
    group: "Atividade",
  },
  {
    tag: "{{item.type}}",
    label: "Atividade: Tipo (Chamada, Reunião...)",
    group: "Atividade",
  },
  {
    tag: "{{item.due_date}}",
    label: "Atividade: Data de Vencimento",
    group: "Atividade",
  },
  {
    tag: "{{item.data_inicio}}",
    label: "Atividade: Início",
    group: "Atividade",
  },
  { tag: "{{item.data_fim}}", label: "Atividade: Fim", group: "Atividade" },
  {
    tag: "{{item.prioridade}}",
    label: "Atividade: Prioridade",
    group: "Atividade",
  },
  {
    tag: "{{item.notes}}",
    label: "Atividade: Observações",
    group: "Atividade",
  },
  {
    tag: "{{item.link_chamada}}",
    label: "Atividade: Link da Chamada",
    group: "Atividade",
  },
  {
    tag: "{{item.done_date}}",
    label: "Atividade: Data de Conclusão",
    group: "Atividade",
  },

  // ── Negócio Direto (quando o gatilho é o Negócio) ──
  { tag: "{{item.name}}", label: "Nome do Negócio", group: "Negócio" },
  { tag: "{{item.stage}}", label: "Etapa do Funil", group: "Negócio" },
  { tag: "{{item.status}}", label: "Status (won/lost/open)", group: "Negócio" },
  { tag: "{{item.amount}}", label: "Valor (R$)", group: "Negócio" },
  {
    tag: "{{item.valor_proposta}}",
    label: "Valor da Proposta",
    group: "Negócio",
  },
  { tag: "{{item.category}}", label: "Categoria", group: "Negócio" },
  { tag: "{{item.description}}", label: "Descrição", group: "Negócio" },
  { tag: "{{item.temperatura}}", label: "Temperatura", group: "Negócio" },
  { tag: "{{item.origem}}", label: "Origem", group: "Negócio" },

  // ── Contato Conectado / Pessoa ──
  {
    tag: "{{item.contact.first_name}}",
    label: "Contato: Primeiro Nome",
    group: "Contato Conectado",
  },
  {
    tag: "{{item.contact.last_name}}",
    label: "Contato: Sobrenome",
    group: "Contato Conectado",
  },
  {
    tag: "{{item.contact.email}}",
    label: "Contato: E-mail",
    group: "Contato Conectado",
  },
  {
    tag: "{{item.contact.phone}}",
    label: "Contato: Telefone",
    group: "Contato Conectado",
  },
  {
    tag: "{{item.contact.title}}",
    label: "Contato: Cargo",
    group: "Contato Conectado",
  },
  { tag: "{{item.first_name}}", label: "Primeiro Nome", group: "Pessoa" },
  { tag: "{{item.last_name}}", label: "Sobrenome", group: "Pessoa" },
  { tag: "{{item.title}}", label: "Cargo", group: "Pessoa" },

  // ── Empresa Conectada / Organização ──
  {
    tag: "{{item.company.name}}",
    label: "Empresa: Razão / Nome",
    group: "Empresa Conectada",
  },
  {
    tag: "{{item.company.sector}}",
    label: "Empresa: Setor",
    group: "Empresa Conectada",
  },
  {
    tag: "{{item.company.phone_number}}",
    label: "Empresa: Telefone",
    group: "Empresa Conectada",
  },
  {
    tag: "{{item.company.city}}",
    label: "Empresa: Cidade",
    group: "Empresa Conectada",
  },
  {
    tag: "{{item.company.state_abbr}}",
    label: "Empresa: Estado (UF)",
    group: "Empresa Conectada",
  },

  // ── Geral ──
  { tag: "{{item.id}}", label: "ID do Registro", group: "Geral" },
  { tag: "{{item.created_at}}", label: "Data de Criação", group: "Geral" },
  { tag: "{{item._crm_id}}", label: "ID da Empresa (CRM)", group: "Geral" },
  { tag: "{{item._evento}}", label: "Evento Disparado", group: "Geral" },

  // ── Histórico de Mudança ──
  { tag: "{{item._antes.stage}}", label: "Etapa Anterior", group: "Histórico" },
  {
    tag: "{{item._antes.status}}",
    label: "Status Anterior",
    group: "Histórico",
  },
  {
    tag: "{{item._antes.amount}}",
    label: "Valor Anterior",
    group: "Histórico",
  },
];

export interface ActionFieldOption {
  value: string;
  label: string;
}

export const BRAZIL_UF_OPTIONS: ActionFieldOption[] = [
  { value: "AC", label: "AC - Acre" },
  { value: "AL", label: "AL - Alagoas" },
  { value: "AP", label: "AP - Amapá" },
  { value: "AM", label: "AM - Amazonas" },
  { value: "BA", label: "BA - Bahia" },
  { value: "CE", label: "CE - Ceará" },
  { value: "DF", label: "DF - Distrito Federal" },
  { value: "ES", label: "ES - Espírito Santo" },
  { value: "GO", label: "GO - Goiás" },
  { value: "MA", label: "MA - Maranhão" },
  { value: "MT", label: "MT - Mato Grosso" },
  { value: "MS", label: "MS - Mato Grosso do Sul" },
  { value: "MG", label: "MG - Minas Gerais" },
  { value: "PA", label: "PA - Pará" },
  { value: "PB", label: "PB - Paraíba" },
  { value: "PR", label: "PR - Paraná" },
  { value: "PE", label: "PE - Pernambuco" },
  { value: "PI", label: "PI - Piauí" },
  { value: "RJ", label: "RJ - Rio de Janeiro" },
  { value: "RN", label: "RN - Rio Grande do Norte" },
  { value: "RS", label: "RS - Rio Grande do Sul" },
  { value: "RO", label: "RO - Rondônia" },
  { value: "RR", label: "RR - Roraima" },
  { value: "SC", label: "SC - Santa Catarina" },
  { value: "SP", label: "SP - São Paulo" },
  { value: "SE", label: "SE - Sergipe" },
  { value: "TO", label: "TO - Tocantins" },
];

export const DEAL_STATUS_OPTIONS: ActionFieldOption[] = [
  { value: "aberto", label: "Em Aberto (Em andamento)" },
  { value: "ganho", label: "🏆 Ganho (Won)" },
  { value: "perdido", label: "❌ Perdido (Lost)" },
];

export const DEAL_TEMPERATURE_OPTIONS: ActionFieldOption[] = [
  { value: "quente", label: "🔥 Quente" },
  { value: "morno", label: "⚡ Morno" },
  { value: "frio", label: "❄️ Frio" },
];

export const DEAL_CATEGORY_OPTIONS: ActionFieldOption[] = [
  { value: "contabilidade-mensal", label: "Contabilidade Mensal" },
  { value: "bpo-financeiro", label: "BPO Financeiro" },
  { value: "abertura-empresa", label: "Abertura de Empresa" },
  { value: "recuperacao-tributaria", label: "Recuperação Tributária" },
  { value: "outro", label: "Outro" },
];

export const DEAL_ORIGIN_OPTIONS: ActionFieldOption[] = [
  { value: "indicacao", label: "Indicação" },
  { value: "google", label: "Google" },
  { value: "instagram", label: "Instagram" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "outbound", label: "Outbound" },
  { value: "outro", label: "Outro" },
];

export const ACTIVITY_TYPE_OPTIONS: ActionFieldOption[] = [
  { value: "chamada", label: "📞 Chamada" },
  { value: "reuniao", label: "🤝 Reunião" },
  { value: "whatsapp", label: "💬 WhatsApp" },
  { value: "email", label: "✉️ E-mail" },
  { value: "follow-up", label: "🔄 Follow-up" },
  { value: "visita", label: "📍 Visita" },
  { value: "tarefa", label: "📋 Tarefa" },
];

export const ACTIVITY_PRIORITY_OPTIONS: ActionFieldOption[] = [
  { value: "baixa", label: "🟢 Baixa" },
  { value: "media", label: "🟡 Média" },
  { value: "alta", label: "🔴 Alta" },
];

export const ACTIVITY_COMPLETED_OPTIONS: ActionFieldOption[] = [
  { value: "false", label: "⏳ Pendente (Em aberto)" },
  { value: "true", label: "✅ Concluída" },
];

export const CONTACT_STATUS_OPTIONS: ActionFieldOption[] = [
  { value: "frio", label: "❄️ Frio" },
  { value: "morno", label: "⚡ Morno" },
  { value: "quente", label: "🔥 Quente" },
  { value: "em-contrato", label: "📝 Em Contrato" },
];

export const CONTACT_GENDER_OPTIONS: ActionFieldOption[] = [
  { value: "male", label: "Masculino" },
  { value: "female", label: "Feminino" },
  { value: "nonbinary", label: "Não-binário / Outro" },
];

export const COMPANY_SECTOR_OPTIONS: ActionFieldOption[] = [
  { value: "servicos", label: "Serviços" },
  { value: "comercio", label: "Comércio" },
  { value: "industria", label: "Indústria" },
  { value: "tecnologia", label: "Tecnologia" },
  { value: "saude", label: "Saúde" },
  { value: "construcao-civil", label: "Construção Civil" },
  { value: "alimentacao", label: "Alimentação" },
  { value: "outro", label: "Outro" },
];

export const COMPANY_SIZE_OPTIONS: ActionFieldOption[] = [
  { value: "1", label: "1 funcionário (MEI / Individual)" },
  { value: "10", label: "2 a 9 funcionários (Micro)" },
  { value: "50", label: "10 a 49 funcionários (Pequena)" },
  { value: "250", label: "50 a 249 funcionários (Média)" },
  { value: "500", label: "250+ funcionários (Grande)" },
];

export const COMPANY_TAX_REGIME_OPTIONS: ActionFieldOption[] = [
  { value: "Simples Nacional", label: "Simples Nacional" },
  { value: "Lucro Presumido", label: "Lucro Presumido" },
  { value: "Lucro Real", label: "Lucro Real" },
  { value: "MEI", label: "MEI" },
];

export type ConditionFieldInputType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "pipeline_select"
  | "stage_select"
  | "sales_select";

export interface ConditionFieldDef {
  field: string;
  label: string;
  type: "texto" | "numero" | "data" | "status";
  inputType: ConditionFieldInputType;
  options?: ActionFieldOption[];
  placeholder?: string;
}

/**
 * Retorna apenas os operadores compatíveis com o formato do campo selecionado.
 */
export function getOperatorsForFieldType(
  type: ConditionFieldDef["type"],
): Array<{ value: ConditionOperator; label: string }> {
  switch (type) {
    case "status":
      return [
        { value: "igual_a", label: "É igual a" },
        { value: "mudou_para", label: "Mudou para" },
        { value: "diferente_de", label: "É diferente de" },
        { value: "valor_anterior_era", label: "Valor anterior era..." },
        { value: "esta_vazio", label: "Está vazio" },
        { value: "nao_esta_vazio", label: "Não está vazio" },
      ];
    case "numero":
      return [
        { value: "igual_a", label: "É igual a (=)" },
        { value: "mudou_para", label: "Mudou para" },
        { value: "diferente_de", label: "É diferente de (≠)" },
        { value: "maior_que", label: "Maior que (>)" },
        { value: "menor_que", label: "Menor que (<)" },
        { value: "maior_ou_igual", label: "Maior ou igual (>=)" },
        { value: "menor_ou_igual", label: "Menor ou igual (<=)" },
        { value: "valor_anterior_era", label: "Valor anterior era..." },
        { value: "esta_vazio", label: "Está vazio" },
        { value: "nao_esta_vazio", label: "Não está vazio" },
      ];
    case "data":
      return [
        { value: "data_igual_a", label: "Data é igual a" },
        { value: "mudou_para", label: "Mudou para" },
        { value: "data_antes_de", label: "Data é antes de" },
        { value: "data_depois_de", label: "Data é depois de" },
        { value: "esta_vazio", label: "Está vazio" },
        { value: "nao_esta_vazio", label: "Não está vazio" },
      ];
    case "texto":
    default:
      return [
        { value: "igual_a", label: "É igual a" },
        { value: "mudou_para", label: "Mudou para" },
        { value: "diferente_de", label: "É diferente de" },
        { value: "contem", label: "Contém" },
        { value: "nao_contem", label: "Não contém" },
        { value: "comeca_com", label: "Começa com" },
        { value: "termina_com", label: "Termina com" },
        { value: "valor_anterior_era", label: "Valor anterior era..." },
        { value: "esta_vazio", label: "Está vazio" },
        { value: "nao_esta_vazio", label: "Não está vazio" },
      ];
  }
}

export const ITEM_AVAILABLE_FIELDS: Record<TriggerItem, ConditionFieldDef[]> = {
  negocio: [
    {
      field: "funil_id",
      label: "Funil de Vendas",
      type: "status",
      inputType: "pipeline_select",
    },
    {
      field: "status",
      label: "Status do Negócio",
      type: "status",
      inputType: "select",
      options: DEAL_STATUS_OPTIONS,
    },
    {
      field: "stage",
      label: "Etapa do Funil",
      type: "status",
      inputType: "stage_select",
    },
    {
      field: "temperatura",
      label: "Temperatura",
      type: "status",
      inputType: "select",
      options: DEAL_TEMPERATURE_OPTIONS,
    },
    {
      field: "category",
      label: "Categoria / Serviço",
      type: "status",
      inputType: "select",
      options: DEAL_CATEGORY_OPTIONS,
    },
    {
      field: "origem",
      label: "Origem do Lead",
      type: "status",
      inputType: "select",
      options: DEAL_ORIGIN_OPTIONS,
    },
    {
      field: "amount",
      label: "Valor Estimado (R$)",
      type: "numero",
      inputType: "number",
      placeholder: "Ex: 5000",
    },
    {
      field: "valor_proposta",
      label: "Valor da Proposta (R$)",
      type: "numero",
      inputType: "number",
      placeholder: "Ex: 4500",
    },
    {
      field: "expected_closing_date",
      label: "Data Prevista de Fechamento",
      type: "data",
      inputType: "date",
    },
    {
      field: "sales_id",
      label: "Responsável (Vendedor)",
      type: "status",
      inputType: "sales_select",
    },
    {
      field: "name",
      label: "Nome / Título do Negócio",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Proposta...",
    },
    {
      field: "description",
      label: "Descrição / Observações",
      type: "texto",
      inputType: "text",
      placeholder: "Texto na descrição...",
    },
    {
      field: "motivo_perda",
      label: "Motivo da Perda",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Preço, Sem retorno...",
    },
  ],
  pessoa: [
    {
      field: "status",
      label: "Status / Temperatura do Contato",
      type: "status",
      inputType: "select",
      options: CONTACT_STATUS_OPTIONS,
    },
    {
      field: "gender",
      label: "Gênero",
      type: "status",
      inputType: "select",
      options: CONTACT_GENDER_OPTIONS,
    },
    {
      field: "sales_id",
      label: "Responsável (Vendedor)",
      type: "status",
      inputType: "sales_select",
    },
    {
      field: "first_name",
      label: "Primeiro Nome",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: João",
    },
    {
      field: "last_name",
      label: "Sobrenome",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Silva",
    },
    {
      field: "title",
      label: "Cargo / Função",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Diretor, Sócio...",
    },
    {
      field: "email",
      label: "E-mail",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: @empresa.com.br",
    },
    {
      field: "phone",
      label: "Telefone / WhatsApp",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: 1199999...",
    },
    {
      field: "background",
      label: "Observações / Histórico",
      type: "texto",
      inputType: "text",
      placeholder: "Texto nas observações...",
    },
  ],
  organizacao: [
    {
      field: "sector",
      label: "Setor de Atuação",
      type: "status",
      inputType: "select",
      options: COMPANY_SECTOR_OPTIONS,
    },
    {
      field: "regime_tributario",
      label: "Regime Tributário",
      type: "status",
      inputType: "select",
      options: COMPANY_TAX_REGIME_OPTIONS,
    },
    {
      field: "size",
      label: "Porte da Empresa",
      type: "status",
      inputType: "select",
      options: COMPANY_SIZE_OPTIONS,
    },
    {
      field: "state_abbr",
      label: "Estado (UF)",
      type: "status",
      inputType: "select",
      options: BRAZIL_UF_OPTIONS,
    },
    {
      field: "faturamento_mensal",
      label: "Faturamento Mensal Estimado (R$)",
      type: "numero",
      inputType: "number",
      placeholder: "Ex: 50000",
    },
    {
      field: "numero_funcionarios",
      label: "Número de Funcionários",
      type: "numero",
      inputType: "number",
      placeholder: "Ex: 15",
    },
    {
      field: "sales_id",
      label: "Responsável (Vendedor)",
      type: "status",
      inputType: "sales_select",
    },
    {
      field: "name",
      label: "Nome / Razão Social da Empresa",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Empresa Ltda",
    },
    {
      field: "city",
      label: "Cidade",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: São Paulo",
    },
    {
      field: "tax_identifier",
      label: "CNPJ / Documento",
      type: "texto",
      inputType: "text",
      placeholder: "00.000.000/0001-00",
    },
    {
      field: "website",
      label: "Website",
      type: "texto",
      inputType: "text",
      placeholder: "https://...",
    },
  ],
  atividade: [
    {
      field: "type",
      label: "Tipo de Atividade",
      type: "status",
      inputType: "select",
      options: ACTIVITY_TYPE_OPTIONS,
    },
    {
      field: "completed",
      label: "Status de Conclusão",
      type: "status",
      inputType: "select",
      options: ACTIVITY_COMPLETED_OPTIONS,
    },
    {
      field: "prioridade",
      label: "Prioridade",
      type: "status",
      inputType: "select",
      options: ACTIVITY_PRIORITY_OPTIONS,
    },
    {
      field: "due_date",
      label: "Data de Vencimento",
      type: "data",
      inputType: "date",
    },
    {
      field: "sales_id",
      label: "Responsável (Vendedor)",
      type: "status",
      inputType: "sales_select",
    },
    {
      field: "text",
      label: "Título / Assunto da Atividade",
      type: "texto",
      inputType: "text",
      placeholder: "Ex: Reunião de alinhamento...",
    },
    {
      field: "notes",
      label: "Notas / Observações",
      type: "texto",
      inputType: "text",
      placeholder: "Texto nas notas...",
    },
    {
      field: "deal.stage",
      label: "Negócio Conectado: Etapa do Funil",
      type: "status",
      inputType: "stage_select",
    },
    {
      field: "deal.status",
      label: "Negócio Conectado: Status",
      type: "status",
      inputType: "select",
      options: DEAL_STATUS_OPTIONS,
    },
    {
      field: "deal.temperatura",
      label: "Negócio Conectado: Temperatura",
      type: "status",
      inputType: "select",
      options: DEAL_TEMPERATURE_OPTIONS,
    },
    {
      field: "deal.amount",
      label: "Negócio Conectado: Valor (R$)",
      type: "numero",
      inputType: "number",
      placeholder: "Ex: 5000",
    },
  ],
};

export interface ActionEntityFieldDef {
  key: string;
  label: string;
  requiredOnCreate?: boolean;
  inputType:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "select"
    | "stage_select"
    | "sales_select";
  placeholder?: string;
  defaultValueOnCreate?: any;
  options?: ActionFieldOption[];
  helperText?: string;
  isCustomField?: boolean;
}

export const ACTION_ENTITY_FIELDS: Record<TriggerItem, ActionEntityFieldDef[]> =
  {
    negocio: [
      {
        key: "name",
        label: "Nome / Título do Negócio",
        requiredOnCreate: true,
        inputType: "text",
        placeholder: "Ex: Oportunidade - {{item.first_name}}",
      },
      {
        key: "stage",
        label: "Etapa do Funil",
        requiredOnCreate: true,
        inputType: "stage_select",
        placeholder: "Selecione a etapa do funil...",
        defaultValueOnCreate: "entrada-de-prospect",
      },
      {
        key: "status",
        label: "Status do Negócio",
        requiredOnCreate: false,
        inputType: "select",
        defaultValueOnCreate: "aberto",
        options: DEAL_STATUS_OPTIONS,
      },
      {
        key: "amount",
        label: "Valor do Negócio (R$)",
        requiredOnCreate: false,
        inputType: "number",
        placeholder: "Ex: 5000 ou {{item.amount}}",
      },
      {
        key: "valor_proposta",
        label: "Valor da Proposta (R$)",
        requiredOnCreate: false,
        inputType: "number",
        placeholder: "Ex: 4500 ou {{item.valor_proposta}}",
      },
      {
        key: "temperatura",
        label: "Temperatura",
        requiredOnCreate: false,
        inputType: "select",
        options: DEAL_TEMPERATURE_OPTIONS,
      },
      {
        key: "category",
        label: "Categoria / Serviço",
        requiredOnCreate: false,
        inputType: "select",
        options: DEAL_CATEGORY_OPTIONS,
      },
      {
        key: "origem",
        label: "Origem do Lead",
        requiredOnCreate: false,
        inputType: "select",
        options: DEAL_ORIGIN_OPTIONS,
      },
      {
        key: "expected_closing_date",
        label: "Previsão de Fechamento",
        requiredOnCreate: false,
        inputType: "date",
        placeholder: "AAAA-MM-DD",
      },
      {
        key: "description",
        label: "Descrição / Observações",
        requiredOnCreate: false,
        inputType: "textarea",
        placeholder: "Detalhes sobre a oportunidade...",
      },
      {
        key: "motivo_perda",
        label: "Motivo da Perda",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: Preço, Sem retorno, Fechou com concorrente...",
      },
      {
        key: "link_proposta",
        label: "Link da Proposta",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "https://...",
      },
      {
        key: "sales_id",
        label: "Responsável (Vendedor)",
        requiredOnCreate: false,
        inputType: "sales_select",
      },
      {
        key: "company_id",
        label: "ID da Organização Vinculada",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.company_id}}",
      },
    ],
    atividade: [
      {
        key: "text",
        label: "Título / Assunto da Atividade",
        requiredOnCreate: true,
        inputType: "text",
        placeholder: "Ex: Ligar para confirmar proposta de {{item.name}}",
      },
      {
        key: "type",
        label: "Tipo de Atividade",
        requiredOnCreate: true,
        inputType: "select",
        defaultValueOnCreate: "chamada",
        options: ACTIVITY_TYPE_OPTIONS,
      },
      {
        key: "prazo_relativo",
        label: "Prazo / Vencimento Relativo",
        requiredOnCreate: true,
        inputType: "select",
        defaultValueOnCreate: "1_dia",
        options: [
          { value: "mesmo_dia", label: "Hoje mesmo (imediato)" },
          { value: "1_dia", label: "+1 dia útil" },
          { value: "2_dias", label: "+2 dias úteis" },
          { value: "3_dias", label: "+3 dias úteis" },
          { value: "1_semana", label: "+1 semana" },
        ],
      },
      {
        key: "prioridade",
        label: "Prioridade",
        requiredOnCreate: false,
        inputType: "select",
        options: ACTIVITY_PRIORITY_OPTIONS,
      },
      {
        key: "notes",
        label: "Notas / Detalhes da Atividade",
        requiredOnCreate: false,
        inputType: "textarea",
        placeholder: "Instruções ou observações para a atividade...",
      },
      {
        key: "due_date",
        label: "Data de Vencimento Fixa",
        requiredOnCreate: false,
        inputType: "date",
        placeholder: "AAAA-MM-DD",
      },
      {
        key: "link_chamada",
        label: "Link da Chamada / Reunião",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "https://meet.google.com/...",
      },
      {
        key: "completed",
        label: "Status de Conclusão",
        requiredOnCreate: false,
        inputType: "select",
        options: ACTIVITY_COMPLETED_OPTIONS,
      },
      {
        key: "sales_id",
        label: "Responsável (Vendedor)",
        requiredOnCreate: false,
        inputType: "sales_select",
      },
      {
        key: "deal_id",
        label: "ID do Negócio Vinculado",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.id}} ou {{item.deal.id}}",
      },
      {
        key: "contact_id",
        label: "ID do Contato Vinculado",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.contact.id}}",
      },
      {
        key: "company_id",
        label: "ID da Organização Vinculada",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.company_id}}",
      },
    ],
    pessoa: [
      {
        key: "first_name",
        label: "Primeiro Nome",
        requiredOnCreate: true,
        inputType: "text",
        placeholder: "Ex: {{item.first_name}} ou João",
      },
      {
        key: "last_name",
        label: "Sobrenome",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.last_name}} ou Silva",
      },
      {
        key: "email",
        label: "E-mail",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: cliente@empresa.com.br ou {{item.contact.email}}",
      },
      {
        key: "phone",
        label: "Telefone / WhatsApp",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: (11) 99999-9999 ou {{item.contact.phone}}",
      },
      {
        key: "title",
        label: "Cargo / Função",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: Sócio-Diretor, Gerente Financeiro...",
      },
      {
        key: "status",
        label: "Status / Temperatura do Contato",
        requiredOnCreate: false,
        inputType: "select",
        options: CONTACT_STATUS_OPTIONS,
      },
      {
        key: "gender",
        label: "Gênero",
        requiredOnCreate: false,
        inputType: "select",
        options: CONTACT_GENDER_OPTIONS,
      },
      {
        key: "linkedin_url",
        label: "LinkedIn (URL)",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "https://linkedin.com/in/...",
      },
      {
        key: "background",
        label: "Observações / Histórico",
        requiredOnCreate: false,
        inputType: "textarea",
        placeholder: "Informações sobre o contato...",
      },
      {
        key: "sales_id",
        label: "Responsável (Vendedor)",
        requiredOnCreate: false,
        inputType: "sales_select",
      },
      {
        key: "company_id",
        label: "ID da Organização Vinculada",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: {{item.company_id}}",
      },
    ],
    organizacao: [
      {
        key: "name",
        label: "Nome / Razão Social da Organização",
        requiredOnCreate: true,
        inputType: "text",
        placeholder: "Ex: {{item.company.name}} ou Empresa Ltda",
      },
      {
        key: "nome_fantasia",
        label: "Nome Fantasia",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: Grupo Dotus",
      },
      {
        key: "tax_identifier",
        label: "CNPJ / Documento",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "00.000.000/0001-00",
      },
      {
        key: "sector",
        label: "Setor de Atuação",
        requiredOnCreate: false,
        inputType: "select",
        options: COMPANY_SECTOR_OPTIONS,
      },
      {
        key: "regime_tributario",
        label: "Regime Tributário",
        requiredOnCreate: false,
        inputType: "select",
        options: COMPANY_TAX_REGIME_OPTIONS,
      },
      {
        key: "size",
        label: "Porte da Empresa",
        requiredOnCreate: false,
        inputType: "select",
        options: COMPANY_SIZE_OPTIONS,
      },
      {
        key: "phone_number",
        label: "Telefone da Empresa",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "(11) 3000-0000",
      },
      {
        key: "website",
        label: "Website",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "https://...",
      },
      {
        key: "faturamento_mensal",
        label: "Faturamento Mensal Estimado (R$)",
        requiredOnCreate: false,
        inputType: "number",
        placeholder: "Ex: 50000",
      },
      {
        key: "numero_funcionarios",
        label: "Número de Funcionários",
        requiredOnCreate: false,
        inputType: "number",
        placeholder: "Ex: 15",
      },
      {
        key: "atividade_principal",
        label: "Atividade Principal (CNAE)",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: Consultoria empresarial",
      },
      {
        key: "city",
        label: "Cidade",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Ex: São Paulo",
      },
      {
        key: "state_abbr",
        label: "Estado (UF)",
        requiredOnCreate: false,
        inputType: "select",
        options: BRAZIL_UF_OPTIONS,
      },
      {
        key: "address",
        label: "Endereço",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "Rua, número, complemento...",
      },
      {
        key: "zipcode",
        label: "CEP",
        requiredOnCreate: false,
        inputType: "text",
        placeholder: "00000-000",
      },
      {
        key: "description",
        label: "Descrição da Empresa",
        requiredOnCreate: false,
        inputType: "textarea",
        placeholder: "Observações sobre a empresa...",
      },
      {
        key: "sales_id",
        label: "Responsável (Vendedor)",
        requiredOnCreate: false,
        inputType: "sales_select",
      },
    ],
  };
